import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, KeyRound, Power, X, BarChart3, Phone } from "lucide-react";
import { api, arabicError } from "../lib/api";
import EmployeeLeavesPanel from "../components/EmployeeLeavesPanel";
import EmployeePerformance from "../components/EmployeePerformance";

const inputCls = "w-full h-11 rounded-lg border border-[#7AB2B2] px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E] bg-white";

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="p-4 border-b border-[#7AB2B2] flex items-center justify-between">
        <h3 className="font-extrabold text-[#333333]">{title}</h3>
        <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center" data-testid="modal-close"><X /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const PasswordFields = ({ form, set, prefix }) => (
  <>
    <input type="password" placeholder="كلمة المرور (8 أحرف على الأقل، حرف ورقم)" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid={`${prefix}-password`} required />
    <input type="password" placeholder="تأكيد كلمة المرور" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} className={inputCls} data-testid={`${prefix}-confirm`} required />
  </>
);

// Employee's OWN WhatsApp number (never the owner's). Stored in employees.phone_number, edited by the owner here.
const EmployeePhone = ({ emp, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(emp.phone_number || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.patch(`/employees/${emp.id}/phone`, { phone_number: v.trim() }); toast.success(r.data.message); setEdit(false); onSaved(); }
    catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };
  if (!edit) return (
    <button onClick={() => { setV(emp.phone_number || ""); setEdit(true); }} className="mt-1 text-xs text-[#09637E] font-bold flex items-center gap-1" data-testid={`employee-phone-edit-${emp.id}`}>
      <Phone size={12} /> {emp.phone_number ? <span dir="ltr">{emp.phone_number}</span> : "إضافة رقم واتساب"}
    </button>
  );
  return (
    <div className="mt-1 flex items-center gap-1" data-testid={`employee-phone-form-${emp.id}`}>
      <input type="tel" value={v} onChange={(e) => setV(e.target.value)} placeholder="09xxxxxxxx" className="h-8 w-40 rounded-md border border-[#7AB2B2] px-2 text-xs" dir="ltr" data-testid={`employee-phone-input-${emp.id}`} />
      <button onClick={save} disabled={busy} className="h-8 px-2 rounded-md bg-[#09637E] text-white text-xs font-bold" data-testid={`employee-phone-save-${emp.id}`}>حفظ</button>
      <button onClick={() => setEdit(false)} className="h-8 px-2 rounded-md border border-[#7AB2B2] text-xs">إلغاء</button>
    </div>
  );
};

export default function Employees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [create, setCreate] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("accounts");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const [branches, setBranches] = useState([]);
  const load = () => api.get("/employees").then((r) => setItems(r.data)).catch((e) => toast.error(arabicError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const mainBranchId = branches.find((b) => b.is_main)?.id || "";
  const setBranch = async (emp, branchId) => {
    try { const r = await api.patch(`/employees/${emp.id}/branch`, { branch_id: branchId || null }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(arabicError(e)); }
  };
  const setDesignation = async (emp, designation) => {
    try { const r = await api.patch(`/employees/${emp.id}/designation`, { designation }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(arabicError(e)); }
  };

  const submitCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/employees", { name: form.name, email: form.email, password: form.password, confirm_password: form.confirm, phone_number: form.phone.trim() || null });
      toast.success("تم إنشاء حساب الموظف"); setCreate(false); setForm({ name: "", email: "", password: "", confirm: "" }); load();
    } catch (err) { toast.error(arabicError(err)); } finally { setSaving(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/employees/${resetFor.id}/password`, { new_password: form.password, confirm_password: form.confirm });
      toast.success("تم تغيير كلمة مرور الموظف"); setResetFor(null); setForm({ name: "", email: "", password: "", confirm: "" });
    } catch (err) { toast.error(arabicError(err)); } finally { setSaving(false); }
  };

  const toggle = async (emp) => {
    const next = !emp.is_active;
    if (!next && !window.confirm(`إيقاف حساب ${emp.name}؟ لن يستطيع تسجيل الدخول حتى تعيد تفعيله.`)) return;
    try {
      const r = await api.patch(`/employees/${emp.id}/status`, { is_active: next });
      toast.success(r.data.message); load();
    } catch (err) { toast.error(arabicError(err)); }
  };

  const togglePerm = async (emp, perm) => {
    const cur = emp.permissions || [];
    const next = cur.includes(perm) ? cur.filter((p) => p !== perm) : [...cur, perm];
    try {
      await api.patch(`/employees/${emp.id}/permissions`, { permissions: next });
      toast.success(next.includes(perm) ? "تم منح الصلاحية" : "تم سحب الصلاحية"); load();
    } catch (err) { toast.error(arabicError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-[#333333] flex items-center gap-2"><Users className="text-[#09637E]" /> الموظفين</h1>
          <p className="text-[#666666] mt-1 text-sm">حسابات الموظفين التابعة لمحلك — صلاحيات محدودة ومنفصلة عن حساب التاجر</p>
        </div>
        <button onClick={() => { setForm({ name: "", email: "", password: "", confirm: "" }); setCreate(true); }} data-testid="add-employee-btn"
          className="h-11 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2">
          <UserPlus size={18} /> إضافة موظف
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-[#EBF4F6] p-1 rounded-lg" data-testid="employees-view-tabs">
        <button onClick={() => setView("accounts")} data-testid="employees-tab-accounts" className={`h-11 rounded-md font-bold flex items-center justify-center gap-1 ${view === "accounts" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}><Users size={16} /> الحسابات والصلاحيات</button>
        <button onClick={() => setView("performance")} data-testid="employees-tab-performance" className={`h-11 rounded-md font-bold flex items-center justify-center gap-1 ${view === "performance" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}><BarChart3 size={16} /> أداء الموظفين</button>
      </div>

      {view === "performance" ? <EmployeePerformance /> : (<>
      <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid="employees-list">
        {loading ? <div className="p-8 text-center text-[#666666]">جارٍ التحميل...</div> : items.length === 0 ? (
          <div className="p-10 text-center text-[#666666]" data-testid="employees-empty">لا يوجد موظفون بعد — أضف أول موظف من الزر أعلاه</div>
        ) : items.map((emp) => (
          <div key={emp.id} className="p-4 border-b border-[#EBF4F6] last:border-0 flex flex-wrap items-center gap-3" data-testid={`employee-row-${emp.id}`}>
            <div className="flex-1 min-w-[160px]">
              <div className="font-bold text-[#333333]">{emp.name}</div>
              <div className="text-sm text-[#666666] break-all" dir="ltr">{emp.email}</div>
              <EmployeePhone emp={emp} onSaved={load} />
            </div>
            <span className={`text-xs font-bold px-2 py-1 rounded ${emp.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`} data-testid={`employee-status-${emp.id}`}>
              {emp.is_active ? "مفعّل" : "موقوف"}
            </span>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-testid={`employee-branch-row-${emp.id}`}>
              <label className="text-xs font-bold text-[#333333] flex items-center gap-1">الفرع
                <select value={emp.branch_id || mainBranchId} onChange={(e) => setBranch(emp, e.target.value)} className="h-9 px-2 rounded-lg border border-[#7AB2B2] text-xs bg-white max-w-[160px]" data-testid={`employee-branch-${emp.id}`}>
                  {branches.length === 0 && <option value="">الفرع الرئيسي</option>}
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
              <label className="text-xs font-bold text-[#333333] flex items-center gap-1">الصفة
                <select value={emp.designation || "employee"} onChange={(e) => setDesignation(emp, e.target.value)} className="h-9 px-2 rounded-lg border border-[#7AB2B2] text-xs bg-white" data-testid={`employee-designation-${emp.id}`}>
                  <option value="employee">موظف</option><option value="branch_manager">مدير فرع</option>
                </select>
              </label>
              {emp.designation === "branch_manager" && <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800" data-testid={`employee-manager-badge-${emp.id}`}>مدير فرع — الصلاحيات كالموظف</span>}
            </div>
            <div className="flex flex-wrap gap-3 w-full sm:w-auto" data-testid={`employee-perms-${emp.id}`}>
              {[["physical_sales", "البيع الفيزيائي", "sales"], ["debt", "الدين", "debt"], ["debt_correct", "تصحيح الدفعات", "debt-correct"], ["returns", "المرتجع", "returns"], ["physical_invoices", "فواتير البيع", "invoices"], ["customer_refunds", "إعادة مبالغ للزبائن", "refunds"]].map(([key, label, tid]) => (
                <label key={key} className="flex items-center gap-1 text-xs font-bold text-[#333333] cursor-pointer" data-testid={`employee-perm-${tid}-${emp.id}`}>
                  <input type="checkbox" checked={(emp.permissions || []).includes(key)} onChange={() => togglePerm(emp, key)} className="w-4 h-4 accent-[#09637E]" /> {label}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setForm({ name: "", email: "", password: "", confirm: "" }); setResetFor(emp); }} data-testid={`employee-reset-${emp.id}`}
                className="h-10 px-3 rounded-lg border-2 border-[#088395] text-[#088395] font-bold text-sm flex items-center gap-1 hover:bg-[#EBF4F6] whitespace-nowrap">
                <KeyRound size={16} /> كلمة المرور
              </button>
              <button onClick={() => toggle(emp)} data-testid={`employee-toggle-${emp.id}`}
                className={`h-10 px-3 rounded-lg border-2 font-bold text-sm flex items-center gap-1 whitespace-nowrap ${emp.is_active ? "border-red-500 text-red-500 hover:bg-red-50" : "border-green-600 text-green-700 hover:bg-green-50"}`}>
                <Power size={16} /> {emp.is_active ? "إيقاف" : "تفعيل"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <EmployeeLeavesPanel />
      </>)}

      {create && (
        <Modal title="إضافة موظف جديد" onClose={() => setCreate(false)} testid="create-employee-modal">
          <form onSubmit={submitCreate} className="space-y-3">
            <input placeholder="اسم الموظف" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="employee-name" required minLength={2} />
            <input type="email" placeholder="البريد الإلكتروني" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="employee-email" required dir="ltr" />
            <input type="tel" placeholder="رقم واتساب الموظف (اختياري، مثال 09xxxxxxxx)" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="employee-phone" dir="ltr" />
            <PasswordFields form={form} set={set} prefix="employee" />
            <button type="submit" disabled={saving} className="w-full h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold disabled:opacity-60" data-testid="employee-submit">
              {saving ? "جارٍ الحفظ..." : "إنشاء الحساب"}
            </button>
          </form>
        </Modal>
      )}
      {resetFor && (
        <Modal title={`كلمة مرور جديدة — ${resetFor.name}`} onClose={() => setResetFor(null)} testid="reset-password-modal">
          <form onSubmit={submitReset} className="space-y-3">
            <PasswordFields form={form} set={set} prefix="reset" />
            <button type="submit" disabled={saving} className="w-full h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold disabled:opacity-60" data-testid="reset-submit">
              {saving ? "جارٍ الحفظ..." : "حفظ كلمة المرور"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
